import { Component, OnInit, ViewEncapsulation, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { pageTransitions } from '../../../animations/page-transitions';
import { ColumnDefAndHeader, DatacenterType } from '../../../models/datacenter-aggrid-view';
import { DatacenterDefaultConfiguration } from '../../../datacenter/datacenter-default-configuration';
import { ColumnAggregator } from '../../../datacenter/save-layout/column-aggregator';
import { SlideButtonComponent } from '../../slide-button/slide-button.component';
import { ProductListViewService } from '../services/product-list-view.service';

@Component({
	selector: 'copo-product-list-save-layout',
	templateUrl: './product-list-save-layout.component.html',
	styleUrl: './product-list-save-layout.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		CdkDropList,
		NgFor,
		CdkDrag,
		MatIconModule,
		NgIf,
		SlideButtonComponent,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,

		MatButtonModule
	]
})
export class ProductListSaveLayoutComponent implements OnInit, OnDestroy {
	@Input() togglePosition = false;
	@Output() readonly typeOfTab = new EventEmitter<string>();
	@Output() readonly currentColumnStateForSorting = new EventEmitter<ColumnAggregator>();
	@Output() readonly togglePositionChange = new EventEmitter<boolean>();
	searchTextControl = new FormControl('');
	columnAggregatorList: ColumnAggregator;
	allColumns: ColumnDefAndHeader[];
	groupIds: string[];
	private searchTextSubscription?: Subscription;

	constructor(
		readonly viewService: ProductListViewService,
		private readonly defaultConf: DatacenterDefaultConfiguration,
		private readonly router: Router,
		private readonly route: ActivatedRoute
	) {
		const { viewToEdit } = this.viewService;
		if (!viewToEdit) {
			throw new Error('no view to edit');
		}
		const datacenterType =
			<DatacenterType | undefined> this.route.snapshot.queryParams['type'] ?? viewToEdit.viewType;
		this.allColumns = this.defaultConf.getAllColumnsAndHeader(datacenterType).sort(
			(c1, c2) => c1.header.localeCompare(c2.header)
		);
		this.groupIds = [
			...new Set(this.allColumns.filter(item => item.groupId !== undefined)
				.map(item => item.groupId ?? ''))
		];
		const columns = viewToEdit.currentView.columnState.filter(col => !this.groupIds.includes(col.groupId ?? ''))
			.map(value => ({
				colId: value.colId,
				header: this.defaultConf.getHeaderForColumn(datacenterType, value.colId),
				pinned: value.pinned,
				sortable: this.defaultConf.getIsColumnSortable(datacenterType, value.colId)
			}));
		this.allColumns = this.allColumns.filter(col => !this.groupIds.includes(col.groupId ?? ''));

		this.columnAggregatorList = new ColumnAggregator(columns.length > 0
			? [...this.allColumns] : []);
		for (const column of columns) {
			this.columnAggregatorList.addVisibleColumn(column);
		}
	}

	ngOnInit() {
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(300),
			distinctUntilChanged()
		).subscribe(value => {
			const searchText = <string | undefined> value;
			if (searchText === undefined) {
				return;
			}
			const allHiddenColumns = this.allColumns.filter(column => {
				const index = this.columnAggregatorList.getColumnsToShow().findIndex(col => col.colId === column.colId);
				return index === -1;
			});
			this.columnAggregatorList.allColumns = allHiddenColumns.filter(column => {
				if (column.header) {
					return column.header.toLowerCase().replace(/\u00A0/g, ' ').search(searchText.toLowerCase()) >= 0;
				}
				return false;
			});
		});
	}

	drop(event: CdkDragDrop<ColumnDefAndHeader[]>) {
		if (event.previousContainer === event.container) {
			moveItemInArray(
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
		} else {
			transferArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
		}
	}

	onToggleChange() {
		this.typeOfTab.emit('sorting');
		this.currentColumnStateForSorting.emit(this.columnAggregatorList);
		this.togglePositionChange.emit(true);
	}

	getColumnState() {
		return this.columnAggregatorList.getColumnsToShow();
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	async close() {
		await this.router.navigate(['../'], { relativeTo: this.route });
	}
}
